import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, ArrowRight, CornerDownLeft } from "lucide-react";
import { api } from "../../lib/api";
import { articleUrl } from "../../lib/format";

export const SearchDialog = ({ open, onOpenChange }) => {
  const [q, setQ] = useState("");
  const [items, setItems] = useState([]);
  const inputRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open) { setTimeout(() => inputRef.current?.focus(), 30); document.body.style.overflow = "hidden"; }
    else { document.body.style.overflow = ""; setQ(""); setItems([]); }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (q.trim().length < 2) { setItems([]); return; }
    const t = setTimeout(() => api.get("/search/suggest", { params: { q } }).then((r) => setItems(r.data)).catch(() => {}), 180);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onOpenChange(false);
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onOpenChange]);

  if (!open) return null;
  const submit = (e) => { e.preventDefault(); if (q.trim()) { navigate(`/search/?q=${encodeURIComponent(q.trim())}`); onOpenChange(false); } };

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center bg-black/40 px-4 pt-[12vh] backdrop-blur-sm" role="dialog" aria-modal="true" aria-label="Search" onClick={() => onOpenChange(false)} data-testid="search-dialog">
      <div className="w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <form onSubmit={submit} className="flex items-center gap-3 border-b border-ab-border px-5">
          <Search size={20} className="text-ab-muted" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search articles, tips and guides…" className="h-16 flex-1 bg-transparent text-lg outline-none placeholder:text-ab-subtle" aria-label="Search query" data-testid="search-dialog-input" />
          <kbd className="hidden rounded-md border border-ab-border px-2 py-1 font-mono text-[10px] text-ab-muted sm:block">ESC</kbd>
        </form>
        {items.length > 0 && (
          <ul className="max-h-[50vh] overflow-auto p-2" data-testid="search-suggestions">
            {items.map((a) => (
              <li key={a.id}>
                <button type="button" onClick={() => { navigate(articleUrl(a)); onOpenChange(false); }} className="flex w-full items-center justify-between gap-4 rounded-xl px-4 py-3 text-left hover:bg-ab-soft" data-testid={`search-suggestion-${a.slug}`}>
                  <span><span className="eyebrow block">{a.category_name}</span><span className="font-serif text-base">{a.title}</span></span>
                  <ArrowRight size={16} className="shrink-0 text-ab-muted" />
                </button>
              </li>
            ))}
          </ul>
        )}
        <div className="flex items-center justify-between px-5 py-3 text-xs text-ab-muted">
          <span>Search across titles, excerpts, body text, categories and tags.</span>
          <span className="flex items-center gap-1"><CornerDownLeft size={12} /> to see all results</span>
        </div>
      </div>
    </div>
  );
};
